// 📄 utils/curlParser.ts
// Parser 100% client-side de comandos cURL. Por defecto solo extrae
// estructura (URL, método, headers, body, query params) — nunca
// credenciales: esas se detectan y se descartan (ver detectedCredentials)
// porque las plantillas del Catálogo de APIs Estándar (ver
// pages/Admin/components/ApiTemplateCard.tsx) son de referencia, sin
// secretos (mismo criterio que catalogoApisService.ts).
// Con opciones.incluirCredenciales = true (usado al importar dentro de
// un proyecto ya creado, ver components/Configuracion/ApiDefinitionCard.tsx
// y components/shared/ImportarCurlModal.tsx) sí se extraen: ese modo
// asume que las credenciales son propias de ese proyecto, no de un
// catálogo compartido.
import { ARIBA_DATA_CENTERS } from "../components/Configuracion/aribaDataCenters.ts";
import { ApiEndpointMetodo, ApiEndpointParam } from "../services/clienteConexionService.ts";

export interface ParsedCredential {
  tipo: "apikey" | "bearer" | "basic";
  header: string;
}

export interface ParseCurlOpciones {
  // Si es true, intenta extraer credenciales reales del cURL (ver
  // credencialesImportadas) en vez de solo detectarlas y descartarlas.
  incluirCredenciales?: boolean;
  // Cómo repartir un "Authorization: Basic" decodificado —
  // 'oauth' → clientId/oauthSecret, 'basic' → usuario/contraseña. El
  // caller (ApiDefinitionForm.tsx/ApiTemplateForm.tsx) ya sabe el
  // authTipo elegido para esta API, así que se lo pasa acá en vez de
  // adivinar. Si no viene (compatibilidad), se infiere por el host (ver
  // esHostAriba) — heurística que ya no alcanza sola desde que un API
  // Ariba también puede ser Basic Auth manual (ver Flow Extension API).
  authTipoEsperado?: "oauth" | "basic";
}

export interface CredencialesImportadas {
  apiKey?: string;
  clientId?: string;
  oauthSecret?: string;
  usuario?: string;
  contraseña?: string;
}

export interface ParsedCurlResult {
  ok: boolean;
  error?: string;
  urlBase?: string;
  api?: string;
  dataCenter?: string;
  tokenUrl?: string;
  grantType?: string;
  metodo?: ApiEndpointMetodo;
  bodyEjemplo?: string;
  params?: ApiEndpointParam[];
  headers?: ApiEndpointParam[];
  // Sugerencia heurística (ver detectarIdHardcodeado): si el último
  // segmento del path parece un ID literal (ej. "/suppliers/20"), se
  // separa de `api` y se propone acá como path variable — nunca se
  // aplica sola, el admin la ve en el preview y decide.
  endpointSugerido?: string;
  pathVariablesSugeridas?: ApiEndpointParam[];
  detectedCredentials: ParsedCredential[];
  // Solo presente con opciones.incluirCredenciales = true y algo que
  // extraer. "Authorization: Basic" se reparte entre clientId/oauthSecret
  // o usuario/contraseña según si el host del cURL parece ser Ariba (ver
  // esHostAriba) — así el admin no tiene que saber la diferencia técnica.
  credencialesImportadas?: CredencialesImportadas;
  warnings: string[];
}

const METODOS_VALIDOS: ApiEndpointMetodo[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];

// Nombres de headers que son credenciales por cliente y nunca deben
// pasar al catálogo estándar (ver ApiDefinition en clienteConexionService.ts).
const HEADERS_CREDENCIALES = ["apikey", "api-key", "x-api-key"];

const quitarComillas = (valor: string): string => {
  const v = valor.trim();
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    return v.slice(1, -1);
  }
  return v;
};

// Tokeniza respetando comillas simples/dobles (los valores de --header
// suelen traer "Content-Type: application/json" completo entre comillas).
const tokenizar = (curl: string): string[] => {
  const tokens: string[] = [];
  const regex = /"([^"]*)"|'([^']*)'|(\S+)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(curl)) !== null) {
    tokens.push(match[1] ?? match[2] ?? match[3]);
  }
  return tokens;
};

// Determina si el cURL apunta a Ariba con solo el hostname — usado más
// abajo para decidir si un "Authorization: Basic" es clientId/oauthSecret
// (Ariba) o usuario/contraseña (todo lo demás). No depende de
// ARIBA_DATA_CENTERS para no perder hosts de Ariba no listados ahí todavía.
const esHostAriba = (hostname: string): boolean => /(^|\.)ariba\.com$/i.test(hostname);

const inferirDataCenter = (hostname: string): { codigo: string; tokenUrl: string } | null => {
  const host = hostname.toLowerCase();
  const dc = ARIBA_DATA_CENTERS.find((d) => host === d.urlBase.replace(/^https?:\/\//, "").toLowerCase());
  if (dc) return { codigo: dc.codigo, tokenUrl: dc.tokenUrl };

  // Fallback por si el host no calza exacto con ARIBA_DATA_CENTERS
  // (p. ej. variantes de subdominio no listadas todavía).
  if (host === "eu.openapi.ariba.com") return { codigo: "EU", tokenUrl: "https://api-eu.ariba.com/v2/oauth/token" };
  if (host === "openapi.ariba.com") return { codigo: "US", tokenUrl: "https://api.ariba.com/v2/oauth/token" };
  if (host === "cn.openapi.ariba.com") return { codigo: "CN", tokenUrl: "" };
  return null;
};

const ID_NUMERICO = /^\d+$/;
const ID_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Singularización simple (suficiente para nombres de recurso en inglés
// típicos de OpenAPIs: "suppliers" → "supplier", "invoices" → "invoice",
// "companies" → "company"). No pretende ser exhaustiva — es solo el
// nombre de partida que el admin ve y puede renombrar en el preview.
const singularizar = (palabra: string): string => {
  if (/[^aeiou]ies$/i.test(palabra)) return palabra.slice(0, -3) + "y";
  if (/(s|x|z|ch|sh)es$/i.test(palabra)) return palabra.slice(0, -2);
  if (/[^s]s$/i.test(palabra)) return palabra.slice(0, -1);
  return palabra;
};

// Heurística opcional: si el último segmento del path importado parece
// un ID literal (numérico o UUID), sugiere convertirlo en path variable
// nombrada a partir del segmento anterior (ej. "suppliers" → "supplierId").
// No es bloqueante ni se auto-aplica — solo puebla endpointSugerido /
// pathVariablesSugeridas para que el preview del modal lo muestre.
const detectarIdHardcodeado = (path: string): { apiSinId: string; endpointSugerido: string; pathVariables: ApiEndpointParam[] } | null => {
  const segmentos = path.split("/").filter(Boolean);
  if (segmentos.length === 0) return null;
  const ultimo = segmentos[segmentos.length - 1];
  if (!ID_NUMERICO.test(ultimo) && !ID_UUID.test(ultimo)) return null;

  const anterior = segmentos.length > 1 ? segmentos[segmentos.length - 2] : "";
  const nombreVar = (anterior ? singularizar(anterior) : "recurso") + "Id";
  const prefijo = segmentos.slice(0, -1).join("/");

  return {
    apiSinId: `/${prefijo}`,
    endpointSugerido: `:${nombreVar}`,
    pathVariables: [{ clave: nombreVar, valor: ultimo }]
  };
};

const formatearBody = (raw: string): string => {
  try {
    const parsed = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
};

export const parseCurl = (curlText: string, opciones?: ParseCurlOpciones): ParsedCurlResult => {
  const incluirCredenciales = opciones?.incluirCredenciales ?? false;
  const authTipoEsperado = opciones?.authTipoEsperado;
  const warnings: string[] = [];
  const detectedCredentials: ParsedCredential[] = [];
  const texto = curlText.trim();

  if (!texto || !/^curl\b/i.test(texto)) {
    return { ok: false, error: "No se pudo parsear el cURL. Verifica que sea un comando curl válido.", detectedCredentials, warnings };
  }

  // Une continuaciones de línea tipo "\\\n" (comunes al copiar de Postman/terminal).
  const normalizado = texto.replace(/\\\s*\n/g, " ");
  const tokens = tokenizar(normalizado);

  let url = "";
  let metodo: ApiEndpointMetodo = "GET";
  const headers: ApiEndpointParam[] = [];
  let bodyRaw = "";
  let apiKeyValor: string | null = null;
  let basicRaw: string | null = null;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (token === "--header" || token === "-H") {
      const valor = quitarComillas(tokens[++i] || "");
      const idx = valor.indexOf(":");
      if (idx === -1) continue;
      const clave = valor.slice(0, idx).trim();
      const val = valor.slice(idx + 1).trim();

      const claveLower = clave.toLowerCase();
      if (HEADERS_CREDENCIALES.includes(claveLower)) {
        detectedCredentials.push({ tipo: "apikey", header: clave });
        if (incluirCredenciales) apiKeyValor = val;
        continue;
      }
      if (claveLower === "authorization") {
        if (/^bearer\s+/i.test(val)) {
          detectedCredentials.push({ tipo: "bearer", header: clave });
          continue;
        }
        if (/^basic\s+/i.test(val)) {
          detectedCredentials.push({ tipo: "basic", header: clave });
          if (incluirCredenciales) basicRaw = val.replace(/^basic\s+/i, "").trim();
          continue;
        }
      }
      headers.push({ clave, valor: val });
      continue;
    }

    if (token === "--request" || token === "-X") {
      const valor = (tokens[++i] || "").toUpperCase() as ApiEndpointMetodo;
      if (METODOS_VALIDOS.includes(valor)) metodo = valor;
      continue;
    }

    if (token === "--data" || token === "--data-raw" || token === "--data-binary" || token === "--data-ascii") {
      bodyRaw = quitarComillas(tokens[++i] || "");
      continue;
    }

    if (token === "--location" || token === "-L" || token === "--compressed" || token === "-s" || token === "--silent") {
      continue;
    }

    if (!url && (token === "curl" || token.startsWith("-"))) {
      continue;
    }

    if (!url && /^https?:\/\//i.test(quitarComillas(token))) {
      url = quitarComillas(token);
    }
  }

  if (!url) {
    return { ok: false, error: "No se pudo parsear el cURL. Verifica que sea un comando curl válido.", detectedCredentials, warnings };
  }

  // Body implica POST si no se especificó --request explícito.
  if (bodyRaw && metodo === "GET") metodo = "POST";

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    return { ok: false, error: "No se pudo parsear el cURL. Verifica que sea un comando curl válido.", detectedCredentials, warnings };
  }

  const urlBase = `${parsedUrl.protocol}//${parsedUrl.host}`;
  const api = parsedUrl.pathname;
  const params: ApiEndpointParam[] = Array.from(parsedUrl.searchParams.entries()).map(([clave, valor]) => ({ clave, valor }));

  const dc = inferirDataCenter(parsedUrl.hostname);
  const result: ParsedCurlResult = {
    ok: true,
    urlBase,
    api,
    metodo,
    bodyEjemplo: bodyRaw ? formatearBody(bodyRaw) : "",
    params,
    headers,
    detectedCredentials,
    warnings
  };

  if (dc) {
    result.dataCenter = dc.codigo;
    if (dc.tokenUrl) result.tokenUrl = dc.tokenUrl;
    result.grantType = "openapi_2lo";
  } else {
    warnings.push("URL no reconocida — revisa los campos manualmente.");
  }

  if (incluirCredenciales && (apiKeyValor || basicRaw)) {
    const credenciales: CredencialesImportadas = {};
    if (apiKeyValor) credenciales.apiKey = apiKeyValor;

    if (basicRaw) {
      try {
        const decodificado = atob(basicRaw);
        const idx = decodificado.indexOf(":");
        if (idx === -1) {
          warnings.push("El header Authorization: Basic no tiene el formato usuario:contraseña esperado — complétalo manualmente.");
        } else {
          const usuarioOClientId = decodificado.slice(0, idx);
          const secreto = decodificado.slice(idx + 1);
          const esOauth = authTipoEsperado ? authTipoEsperado === "oauth" : (esHostAriba(parsedUrl.hostname) || !!dc);
          if (esOauth) {
            credenciales.clientId = usuarioOClientId;
            credenciales.oauthSecret = secreto;
          } else {
            credenciales.usuario = usuarioOClientId;
            credenciales.contraseña = secreto;
          }
        }
      } catch {
        warnings.push("No se pudo decodificar el header Authorization: Basic — complétalo manualmente.");
      }
    }

    if (Object.keys(credenciales).length > 0) result.credencialesImportadas = credenciales;
  }

  const idDetectado = detectarIdHardcodeado(api);
  if (idDetectado) {
    result.api = idDetectado.apiSinId;
    result.endpointSugerido = idDetectado.endpointSugerido;
    result.pathVariablesSugeridas = idDetectado.pathVariables;
    warnings.push(
      `Se detectó un ID en el path ("${idDetectado.pathVariables[0].valor}") — se sugirió como path variable ` +
      `${idDetectado.endpointSugerido} (valor ${idDetectado.pathVariables[0].valor}). Revísalo antes de guardar.`
    );
  }

  return result;
};
